import { useSyncExternalStore } from "react";
import { SHOP_CART_MAX_LINES, SHOP_CART_MAX_QUANTITY, type ShopCartLine } from "@gooner/shared";

/** Where the buyer's transaction waits out the trip to the processor: the checkout's return
 *  page may strip any query string we hand it. Read by ShopThanks. */
export const SHOP_TXN_KEY = "gp:shop:txn";

const KEY = "gp:shop:cart";
const EMPTY: ShopCartLine[] = [];

/**
 * The cart: slugs and quantities in this browser, nothing else. Names and prices are read
 * fresh when the cart is shown, and the server prices the order itself, so a stale tab can
 * never quote a buyer an old price. Storage can be blocked, so it falls back to memory.
 */
const read = (): ShopCartLine[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? "[]") as ShopCartLine[];
    if (!Array.isArray(parsed)) return EMPTY;
    return parsed.filter((l) => typeof l?.slug === "string" && Number.isInteger(l?.quantity) && l.quantity > 0);
  } catch {
    return EMPTY;
  }
};

let lines = read();
const listeners = new Set<() => void>();

const publish = (next: ShopCartLine[]) => {
  lines = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* blocked storage: the cart lasts until the tab closes */
  }
  for (const l of listeners) l();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  const fromOtherTab = (e: StorageEvent) => {
    if (e.key === KEY) {
      lines = read();
      listener();
    }
  };
  window.addEventListener("storage", fromOtherTab);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", fromOtherTab);
  };
};

export const useShopCart = () => useSyncExternalStore(subscribe, () => lines);

export function setShopCartQuantity(slug: string, quantity: number): void {
  const capped = Math.min(quantity, SHOP_CART_MAX_QUANTITY);
  if (capped <= 0) publish(lines.filter((l) => l.slug !== slug));
  else if (lines.some((l) => l.slug === slug)) publish(lines.map((l) => (l.slug === slug ? { slug, quantity: capped } : l)));
  else if (lines.length < SHOP_CART_MAX_LINES) publish([...lines, { slug, quantity: capped }]);
}

export function addToShopCart(slug: string): void {
  setShopCartQuantity(slug, (lines.find((l) => l.slug === slug)?.quantity ?? 0) + 1);
}

export const clearShopCart = () => publish([]);

/*
 * ORDERS ON THIS DEVICE. A guest has no account to keep their orders on, so this browser
 * keeps each order's key: the same capability as the private link, so it is treated like
 * one (never sent anywhere but the order routes, never put in analytics).
 */
const ORDERS_KEY = "gp:shop:orders";
const PENDING_KEY = "gp:shop:pending";

export interface SavedShopOrder {
  id: string;
  key: string;
}

const readJson = <T,>(store: Storage, key: string, fallback: T): T => {
  try {
    return (JSON.parse(store.getItem(key) ?? "null") as T) ?? fallback;
  } catch {
    return fallback;
  }
};

export function savedShopOrders(): SavedShopOrder[] {
  const list = readJson<SavedShopOrder[]>(localStorage, ORDERS_KEY, []);
  return Array.isArray(list) ? list.filter((o) => typeof o?.id === "string" && typeof o?.key === "string") : [];
}

export function rememberShopOrder(id: string, key: string): void {
  const rest = savedShopOrders().filter((o) => o.id !== id);
  try {
    localStorage.setItem(ORDERS_KEY, JSON.stringify([{ id, key }, ...rest].slice(0, 50)));
  } catch {
    /* blocked storage: the private link and Find an order still work */
  }
}

export const shopOrderKeyFor = (id: string) => savedShopOrders().find((o) => o.id === id)?.key ?? null;

/** The checkout on its way to the processor: its transaction and the key it was given. */
export function rememberPendingCheckout(txn: string, key: string): void {
  try {
    sessionStorage.setItem(SHOP_TXN_KEY, txn);
  } catch {
    /* blocked storage: the thanks page points at Find an order instead */
  }
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ txn, key }));
  } catch {
    /* as above */
  }
}

/** The return page may open in another tab, which has no sessionStorage of its own. */
export function pendingCheckout(): { txn: string; key: string | null } | null {
  let txn: string | null = null;
  try {
    txn = sessionStorage.getItem(SHOP_TXN_KEY);
  } catch {
    txn = null;
  }
  const saved = readJson<{ txn?: string; key?: string } | null>(localStorage, PENDING_KEY, null);
  if (!txn && saved?.txn) txn = saved.txn;
  if (!txn) return null;
  return { txn, key: saved?.txn === txn && typeof saved.key === "string" ? saved.key : null };
}
